import React, { useId, useRef, useLayoutEffect, useState } from "react";

const CylinderSvg = ({
  fillRatio = 0,
  height = 150,
  divisions = 4,
  fulls = 0,
  empties = 0,
}) => {
  const id = useId().replace(/:/g, "");

  /* ---------- Clamp & visual ratio ---------- */
  const rawRatio = Math.max(0, Math.min(1, fillRatio));
  const MAX_VISUAL_FILL = 0.82;
  const visualRatio = rawRatio * MAX_VISUAL_FILL;

  /* ---------- Body metrics ---------- */
  const bodyTop = 800;
  const bodyBottom = 4800;
  const bodyHeight = bodyBottom - bodyTop;

  /* ---------- ACTUAL CENTER X of cylinder ---------- */
  const bodyRef = useRef(null);
  const [centerX, setCenterX] = useState(2500);


  /* ---------- Center positions ---------- */
  const fullsY = bodyTop + (visualRatio * bodyHeight) / 2;
  const emptiesY =
    bodyTop +
    visualRatio * bodyHeight +
    ((1 - visualRatio) * bodyHeight) / 2;

  /* ---------- Responsive text ---------- */
  const percentFontSize = Math.max(300, height * 4.8);
  const percentStroke = Math.max(20, height * 0.28);

  /* ---------- Size ---------- */
  const aspectRatio = 80 / 130;
  const width = height * aspectRatio;

  /* ---------- Paths ---------- */
  const mainPath =
    "M1660 5725 c-104 -32 -180 -138 -180 -250 1 -102 51 -184 142 -229 49 -24 61 -26 200 -26 l148 0 0 -253 0 -254 -42 -12 c-62 -19 -203 -86 -267 -128 -90 -59 -251 -226 -304 -318 -65 -111 -106 -214 -128 -322 -19 -89 -19 -153 -17 -1470 l3 -1378 27 -73 c38 -104 83 -176 158 -252 78 -77 168 -132 267 -163 l73 -22 0 -138 c0 -132 1 -139 24 -162 13 -13 39 -26 57 -30 19 -3 560 -4 1202 -3 l1169 3 29 33 c29 32 29 32 29 166 l0 134 47 11 c101 26 233 107 312 191 38 41 80 106 112 174 62 132 60 73 57 1579 l-3 1372 -28 90 c-51 165 -143 323 -255 438 -103 106 -273 214 -404 257 l-68 22 0 254 0 254 148 0 c166 0 202 10 264 72 53 54 73 102 73 183 0 111 -45 182 -149 238 l-41 22 -1305 2 c-1096 1 -1312 0 -1350 -12z";

  const accentPath =
    "M2420 4980 l0 -180 133 0 132 0 -3 137 -3 138 286 -2 287 -3 1 -135 2 -136 155 0 155 -1 0 181 0 181 -572 0 -573 0 0 -180z";

  useLayoutEffect(() => {
    if (bodyRef.current) {
      const bbox = bodyRef.current.getBBox();
      setCenterX(bbox.x + bbox.width / 2);
    }
  }, []);

  return (
    <svg
      viewBox="1450 0 3100 6000"
      height={height}
      width={width}
      preserveAspectRatio="xMidYBottom meet"
      style={{
        display: "block",
        overflow: "visible",
        transform: "scale(1.08)",
        transformOrigin: "center bottom",
      }}
    >
      <style>
        {`
    .cyl-values {
      opacity: 0;
      transition: opacity 0.18s ease-in-out;
      pointer-events: none;
    }

    .cyl-hover:hover .cyl-values {
      opacity: 1;
    }
  `}
      </style>

      {/* ================== DEFS ================== */}
      <defs>
        {/* Fill gradient */}
        <clipPath id={`clip-full-${id}`}>
          <rect
            x="0"
            y={bodyTop}
            width="5000"
            height={visualRatio * bodyHeight}
          />
        </clipPath>

        <clipPath id={`clip-empty-${id}`}>
          <rect
            x="0"
            y={bodyTop + visualRatio * bodyHeight}
            width="5000"
            height={(1 - visualRatio) * bodyHeight}
          />
        </clipPath>



        {/* Metallic shine */}
        <linearGradient id={`shine-${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="rgba(255,255,255,0.05)" />
          <stop offset="20%" stopColor="rgba(255,255,255,0.35)" />
          <stop offset="50%" stopColor="rgba(255,255,255,0.15)" />
          <stop offset="80%" stopColor="rgba(255,255,255,0.35)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
        </linearGradient>

        {/* Gloss overlay */}
        <linearGradient id={`gloss-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.45)" />
          <stop offset="40%" stopColor="rgba(255,255,255,0.15)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
      </defs>

      {/* ================== BODY ================== */}
      <g
        transform="translate(0,6000) scale(1,-1)"
        className="cyl-hover"
      >

        {/* Base fill */}
        {/* FULLS (GREEN) */}
        <path
          ref={bodyRef}
          d={mainPath}
          fill="#22c55e"
          clipPath={`url(#clip-full-${id})`}
        />

        <path
          d={mainPath}
          fill="#ef4444"
          clipPath={`url(#clip-empty-${id})`}
        />

        {/* Metallic shine */}
        <path d={mainPath} fill={`url(#shine-${id})`} />

        {/* Gloss polish */}
        <path d={mainPath} fill={`url(#gloss-${id})`} />

        {/* ✅ REMOVED Division markers section */}

        {/* Top accent */}
        <path d={accentPath} fill="#020617" />

        {/* Outline */}
        <path
          d={mainPath}
          fill="none"
          stroke="#0f766e"
          strokeWidth="48"
          strokeLinejoin="round"
        />

        {/* FULLS % - Perfectly centered */}
        {fulls > 0 && visualRatio > 0.05 && (
          <g className="cyl-values">
            <text
              x={centerX}
              y={fullsY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={percentFontSize}
              fontWeight="900"
              fill="none"
              stroke="#ffffff"
              strokeWidth={percentStroke * 2.2}
              strokeLinejoin="round"
              transform={`scale(1, -1) translate(0, ${-fullsY * 2})`}
            >
              {fulls}
            </text>

            <text
              x={centerX}
              y={fullsY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={percentFontSize}
              fontWeight="900"
              fill="#065f46"
              transform={`scale(1, -1) translate(0, ${-fullsY * 2})`}
            >
              {fulls}
            </text>
          </g>
        )}


        {/* EMPTIES % - Perfectly centered */}
        {empties > 0 && visualRatio < 0.95 && (
          <g className="cyl-values">
            <text
              x={centerX}
              y={emptiesY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={percentFontSize}
              fontWeight="900"
              fill="none"
              stroke="#ffffff"
              strokeWidth={percentStroke * 2}
              strokeLinejoin="round"
              transform={`scale(1, -1) translate(0, ${-emptiesY * 2})`}
            >
              {empties}
            </text>

            <text
              x={centerX}
              y={emptiesY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={percentFontSize}
              fontWeight="900"
              fill="#991b1b"
              transform={`scale(1, -1) translate(0, ${-emptiesY * 2})`}
            >
              {empties}
            </text>
          </g>
        )}

      </g>
    </svg>
  );
};

export default CylinderSvg;